import * as React from "react";
import { useApiService, useDeleteApi, useTl } from "../../../../../hooks";
import { CountryApi, CountryPedigreePrefixGridDto, PedigreePrefixApi, PedigreePrefixSearch } from "../../../../../api";
import { ButtonContainer, FieldSet, IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import { ETLCodes } from "../../../../../locales";
import { CustomDataTable, DeleteButton, EditButton, ExportButton } from "../../../../../components";
import { ButtonGroup, Checkbox, Classes, Colors, Dialog, Intent } from "@blueprintjs/core";
import { useGlobalData } from "../../../../../contexts";
import { PedigreePrefixPanel } from "./PedigreePrefixPanel";

export interface ICountryPedigreesListProps {
  countryId: number;
  editMode: boolean;
}

export const CountryPedigreesList: React.FunctionComponent<ICountryPedigreesListProps> = ({ countryId, editMode }) => {
  const api = useApiService(PedigreePrefixApi);
  const countryApi = useApiService(CountryApi);
  const { t } = useTl();

  const [dialogOpened, setDialogOpened] = React.useState(false);
  const [belgianRequest, setBelgianRequest] = React.useState(true);
  const [belgian, setBelgian] = React.useState<boolean>();
  const [sobjState, setsobjState] = React.useState<any>();
  const { currentLanguage } = useGlobalData();
  React.useEffect(() => {
    if (belgianRequest) {
      countryApi.isBelgian(countryId).then((result) => setBelgian(result.value));
      setBelgianRequest(false);
    }
  }, [belgian, belgianRequest, countryApi, countryId]);

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [5],
    pageSize: 5,
    sortKeys: { prefix: "ASC" },
  });

  const searchFunction = React.useCallback(
    (sObj?: PedigreePrefixSearch) => {
      setsobjState(PedigreePrefixSearch.fromJS({ ...sObj, countryId: countryId }));
      return api.baseSearch(PedigreePrefixSearch.fromJS({ ...sObj, countryId: countryId }));
    },
    [api, countryId]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });
  React.useEffect(() => {
    !dialogOpened && search();
  }, [dialogOpened, search]);
  const { deleteItem } = useDeleteApi<number>(
    React.useMemo(
      () => ({
        deleteApiFn: (data) => api.deletePedigreePrefix(data),
        onDeletedFn: () => search(),
      }),
      [api, search]
    )
  );

  const columns = React.useMemo<IDataTableColumn<CountryPedigreePrefixGridDto>[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        hidden: !editMode,
        render: (row: CountryPedigreePrefixGridDto) => (
          <ButtonContainer>
            <DeleteButton minimal={true} onDelete={() => deleteItem(row.countryPedigreePrefixId)} />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.Initiales),
        fieldName: "prefix",
      },
      {
        header: () => t(ETLCodes.NextValue),
        fieldName: "nextValue",
        hidden: !belgian,
      },
      {
        header: () => t(ETLCodes.FciRecognized),
        fieldName: "fciRecognized",
        render: (row) => <Checkbox checked={row.fciRecognized} disabled />,
      },
    ],
    [belgian, deleteItem, editMode, t]
  );

  const addItemFunction = React.useCallback(() => {
    setDialogOpened(true);
  }, []);

  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);
  return (
    <>
      <FieldSet
        title={t(ETLCodes.PedigreePrefixes)}
        rightElement={
          <ButtonGroup>
            <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sobjState} />
            {editMode && !belgian && (
              <EditButton
                onClick={(e) => {
                  e.stopPropagation();
                  addItemFunction();
                }}
                text={t(ETLCodes.General_Add)}
                intent={Intent.PRIMARY}
              />
            )}
          </ButtonGroup>
        }
      >
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>

      <Dialog
        title={t(ETLCodes.PedigreePrefixSelection)}
        isOpen={dialogOpened}
        onClose={() => setDialogOpened(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        className={Classes.DIALOG}
        style={{ width: 1000, height: 550, background: Colors.WHITE }}
      >
        <div className={Classes.DIALOG_BODY} style={{ height: 200 }}>
          <PedigreePrefixPanel
            onSaved={() => {
              setDialogOpened(false);
              search();
            }}
            countryId={countryId}
          />
        </div>
      </Dialog>
    </>
  );
};
